import { Injectable } from '@angular/core';
import { catchError, finalize, first, forkJoin, map, Observable, of, ReplaySubject, shareReplay, tap } from 'rxjs';
import { IKeyValue } from '../data/Ikey-value';   
import { IVisaType } from '../data/visa-type';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IPersonType } from '../data/person-type';
import { ILookup } from '../data/Ilookup';
import { environment } from '../../../environments/environment';
import { LookupItem } from '../models/lookup-item';
import { OptionItem } from '../data/option-item';
import { LookupData } from '../data/lookup-data';



@Injectable({
  providedIn: 'root'
})
export class LookupDataService {

 private countries: LookupItem[] | null = null;
  private visaTypes: IVisaType[] | null = null;
  

  private lookupDataObservable: Observable<LookupData> | null = null;


  constructor(
    private http: HttpClient
  ) { }

  loadLookupData(): Observable<LookupData> {
  
  if (this.lookupDataObservable) {
    console.log('Returning cached lookup data');
    return this.lookupDataObservable;
  }


  this.lookupDataObservable = forkJoin({
    maritalStatus: this.getMaritalStatuses(),
    titles: this.getTitles(),
    qualificationsList: this.getEducationLevels(),
    relationTypes: this.getRelationTypes(),
    visaStatus: this.getVisaStatuses(),
    employmentStatus: this.getEmploymentStatuses(),
    childRelationTypes: this.getChildRelationTypes(),
    parentRelationTypes: this.getParentRelationTypes(),
    phoneTypes: this.getPhoneTypes(),
    businessExperience: this.getBusinessExperience(),
    relationshipStatuses: this.getRelationshipStatuses(),
    industries: this.getIndustryTypes(),
    ownershipInterests: this.getOwnershipTypes(),
    assetAmounts: this.getAssetAmounts(),
    appealDecisionBy: this.getAppealDecisionBy(),
    notifiedBy: this.getDecisionNotifiedBy(),
    countries: this.getCountries().pipe(first()),
    visaTypes: this.getVisaTypes().pipe(first()) 
  }).pipe(
    shareReplay(1)
  );

  return this.lookupDataObservable;
}

  getCurrentCountries(): Observable<LookupItem[]> {
    if (this.countries) {
      return of(this.countries);
    }
    return this.getCountries();
  }


    getCurrentVisaTypes(): Observable<IVisaType[]> {
    if (this.visaTypes) {
      return of(this.visaTypes);
    }
    return this.getVisaTypes();
  }

getCountries(): Observable<LookupItem[]> {
  return this.http.get<IKeyValue[]>(`${environment.apiUrl}/AssessmentForms/GetCountryList`).pipe(
    map(response => {
      const mappedResponse = response.map(country => ({
        id: country.key,
        value: country.value,
      } as LookupItem));
      
      return mappedResponse;
    }),
    catchError(error => {
      console.error('Error loading countries:', error);
      throw error;
    })

  );
}

getVisaTypes(): Observable<IVisaType[]> {
  return this.http.get<any[]>(`${environment.apiUrl}/AssessmentForms/GetVisaTypes`).pipe(
    map(response => response.map(visaType => ({
      id: visaType.id,
      VisaType: visaType.visaType,
    } as IVisaType))),
    catchError(error => {
      console.error('Error loading visa types:', error);
      throw error;
    })
  );
}

  getPersonTypes(): Observable<IPersonType> {
    return of({
      parent: 'parent',
      child: 'child',
      other: 'other',
      applicant: 'applicant',
      sponsor: 'sponsor',
      notIncludedChild: 'notIncludedChild'
    });
  }

  getMaritalStatuses(): Observable<ILookup[]> {
    return of([
      { id: 'engaged', value: 'Engaged' },
      { id: 'married', value: 'Married' },
      { id: 'de-facto', value: 'In a De Facto Relationship' },
      { id: 'interdependent', value: 'Interdependent Relationship' },
      { id: 'separated', value: 'Separated' },
      { id: 'divorced', value: 'Divorced' },
      { id: 'widowed', value: 'Widowed' },
      { id: 'never-married', value: 'Never Married' }
    ]);
  }

  getRelationshipStatuses(): Observable<OptionItem[]> {
    return of([
      { value: 'engaged', label: 'Engaged' },
      { value: 'married', label: 'Married' },  
      { value: 'de-facto', label: 'In a De Facto Relationship' },
      { value: 'interdependent', label: 'Interdependent Relationship' }
      
    ]);
  }

  getTitles(): Observable<ILookup[]> {
    return of([
    { id: 'Mr', value: 'Mr' },
    { id: 'Mrs', value: 'Mrs' },
    { id: 'Miss', value: 'Miss' },
    { id: 'Ms', value: 'Ms' },
    { id: 'Dr', value: 'Dr' },
    { id: 'Other', value: 'Other' }
    ]);
  }

  getEducationLevels(): Observable<ILookup[]> {
    return of([
      { id: 'primary-school', value: 'Primary School' },
      { id: 'middle-school', value: 'Middle School' },
      { id: 'high-school', value: 'High School' },
      { id: 's-high-school', value: 'Senior High School' },
      { id: 'trade-apprenticeship', value: 'Trade Apprenticeship' },
      { id: 'training-ex', value: 'Training (External)' },
      { id: 'training-in', value: 'Training (On the Job)' },
      { id: 'cert1', value: 'Certificate I' },
      { id: 'cert2', value: 'Certificate II' },
      { id: 'cert3', value: 'Certificate III' },
      { id: 'cert4', value: 'Certificate IV' },
      { id: 'diploma', value: 'Diploma' },
      { id: 'adv-diploma', value: 'Advanced Diploma' },
      { id: 'associate', value: 'Associate Degree' },
      { id: 'bachelor', value: 'Bachelor Degree' },
      { id: 'honours', value: 'Honours Degree' },
      { id: 'grad-cert', value: 'Graduate Certificate' },
      { id: 'grad-diploma', value: 'Graduate Diploma' },
      { id: 'postgrad-cert', value: 'Postgraduate Certificate' },
      { id: 'postgrad-dip', value: 'Postgraduate Diploma' },
      { id: 'masters', value: 'Masters Degree' },
      { id: 'phd', value: 'Doctoral Degree' },
      { id: 'other', value: 'Other' }
    ]);
  }

  getRelationTypes(): Observable<ILookup[]> {
    return of([
      { id: 'spouse-partner', value: 'Spouse/Partner' },
      { id: 'former-spouse-partner', value: 'Former Spouse/Partner' },
      { id: 'child', value: 'Child' },
      { id: 'parent', value: 'Parent' },
      { id: 'step-parent', value: 'Step Parent' },
      { id: 'step-child', value: 'Step Child' },
      { id: 'grand-parent', value: 'Grand Parent' },
      { id: 'grand-child', value: 'Grand Child' },
      { id: 'adopted-parent', value: 'Adopted Parent' },
      { id: 'adopted-child', value: 'Adopted Child' },
      { id: 'brother', value: 'Brother' },
      { id: 'sister', value: 'Sister' },
      { id: 'step-brother', value: 'Step-brother' },
      { id: 'step-sister', value: 'Step-sister' },
      { id: 'half-brother', value: 'Half-Brother' },
      { id: 'half-sister', value: 'Half-Sister' },
      { id: 'aunt', value: 'Aunt' },
      { id: 'uncle', value: 'Uncle' },
      { id: 'niece', value: 'Niece' },
      { id: 'nephew', value: 'Nephew' },
      { id: 'step-grandparent', value: 'Step-grandparent' },
      { id: 'step-grandchild', value: 'Step-grandchild' },
      { id: 'step-aunt', value: 'Step-aunt' },
      { id: 'step-uncle', value: 'Step-uncle' },
      { id: 'step-niece', value: 'Step-niece' },
      { id: 'step-nephew', value: 'Step-nephew' },
      { id: 'mother-in-law', value: 'Mother-in-Law' },
      { id: 'father-in-law', value: 'Father-in-Law' },
      { id: 'daughter-in-law', value: 'Daughter-in-Law' },
      { id: 'son-in-law', value: 'Son-in-Law' },
      { id: 'sister-in-law', value: 'Sister-in-Law' },
      { id: 'brother-in-law', value: 'Brother-in-Law' },
      { id: 'cousin', value: 'Cousin' },
      { id: 'friend', value: 'Friend' },
      { id: 'associate', value: 'Associate' },
      { id: 'acquaintance', value: 'Acquaintance' },
      { id: 'guardian', value: 'Guardian' },
      { id: 'ward', value: 'Ward' },
      { id: 'adopted-brother', value: 'Adopted Brother' },
      { id: 'adopted-sister', value: 'Adopted Sister' },
      { id: 'other', value: 'Other' }
    ]);
  }

  getChildRelationTypes(): Observable<ILookup[]> {
    return of([
      { id: 'child', value: 'Child' },
      { id: 'step-child', value: 'Step Child' },
      { id: 'adopted-child', value: 'Adopted Child' },
      { id: 'daughter-in-law', value: 'Daughter-in-Law' },
      { id: 'son-in-law', value: 'Son-in-Law' },
      { id: 'other', value: 'Other' }
    ]);
  }

  getParentRelationTypes(): Observable<ILookup[]> {
    return of([
      { id: 'parent', value: 'Parent' },
      { id: 'step-parent', value: 'Step Parent' },
      { id: 'adopted-parent', value: 'Adopted Parent' },
      { id: 'mother-in-law', value: 'Mother-in-Law' },
      { id: 'father-in-law', value: 'Father-in-Law' },
      { id: 'guardian', value: 'Guardian' },
      { id: 'other', value: 'Other' }
    ]);
  }

  getVisaStatuses(): Observable<ILookup[]> {
    return of([
      { id: 'citizen', value: 'Australian Citizen' },
      { id: 'resident', value: 'Australian Permanent Resident' },
      { id: 'nz', value: 'New Zealand Citizen' },
      { id: 'temp', value: 'Australian Temporary Visa Holder' },
      { id: 'other', value: 'Other' }
    ]);
  }

  getEmploymentStatuses(): Observable<ILookup[]> {
    return of([
      { id: 'full-time', value: 'Employed Full Time' },
      { id: 'part-time', value: 'Employed Part Time' },
      { id: 'student', value: 'Student' },
      { id: 'self-employed', value: 'Self Employed' },
      { id: 'unemployed', value: 'Unemployed' },
      { id: 'looking', value: 'Unemployed and looking for work' },
      { id: 'homemaker', value: 'Homemaker' },
      { id: 'retired', value: 'Retired' }
    ]);
  }

  getIndustryTypes(): Observable<ILookup[]> {
    return of([
      { id: 'a', value: 'A - Agriculture, Forestry and Fishing' },
      { id: 'b', value: 'B - Mining' },
      { id: 'c', value: 'C - Manufacturing' },
      { id: 'd', value: 'D - Electricity, Gas, Water and Waste Services' },
      { id: 'e', value: 'E - Construction' },
      { id: 'f', value: 'F - Wholesale Trade' },
      { id: 'g', value: 'G - Retail Trade' },
      { id: 'h', value: 'H - Accommodation and Food Services' },
      { id: 'i', value: 'I - Transport Postal and Warehousing' },
      { id: 'j', value: 'J - Information Media and Telecommunications' },
      { id: 'k', value: 'K - Financial and Insurance Services' },
      { id: 'l', value: 'L - Rental, Hiring and Real Estate Services' },
      { id: 'm', value: 'M - Professional, Scientific and Technical' },
      { id: 'n', value: 'N - Administrative and Support Services' },
      { id: 'o', value: 'O - Public Administration and Safety' },
      { id: 'p', value: 'P - Education and Training' },
      { id: 'q', value: 'Q - Health Care and Social Assistance' },
      { id: 'r', value: 'R - Arts and Recreation Services' },
      { id: 's', value: 'S - Other Services' }
    ]);
  }

  getOwnershipTypes(): Observable<ILookup[]> {
    return of([
      { id: 'company', value: 'Company' },
      { id: 'franchise', value: 'Franchise' },
      { id: 'partnership', value: 'Partnership' },
      { id: 'proprietary', value: 'Proprietary Company' },
      { id: 'public', value: 'Public Company' },
      { id: 'sole', value: 'Sole Proprietor/Sole Trader' },
      { id: 'statutory', value: 'Government Department' },
      { id: 'trust', value: 'Business Trust' },
      { id: 'other', value: 'Other' }
    ]);
  }

  getAssetAmounts(): Observable<ILookup[]> {
    return of([
      { id: '0', value: 'less than $199,000.99' },
      { id: '2', value: '$200,000.00 - $399,999.99' },
      { id: '4', value: '$400,000.00 - $999,999.99' },
      { id: '10', value: '$1,000,000.00 - $1,999,999.99' },
      { id: '20', value: '$2,000,000.00 and above' }
    ]);
  }

  getPhoneTypes(): Observable<OptionItem[]> {
    return of([
      { value: 'Home', label: 'Home' },
      { value: 'Work', label: 'Work' },
      { value: 'Mobile', label: 'Mobile' }
    ]);
  }

  getBusinessExperience(): Observable<ILookup[]> {
    return of([
      { id: "0", value: "None" },
      { id: "1", value: "At least 1 of the past 10 years" },
      { id: "2", value: "At least 2 of the past 10 years" },
      { id: "3", value: "At least 3 of the past 10 years" },
      { id: "4", value: "At least 4 of the past 10 years" },
      { id: "5", value: "At least 5 of the past 10 years" },
      { id: "6", value: "At least 6 of the past 10 years" },
      { id: "7", value: "At least 7 of the past 10 years" },
      { id: "8", value: "At least 8 of the past 10 years" },
      { id: "9", value: "At least 9 of the past 10 years" },
      { id: "10", value: "At least 10 of the past 10 years" },
      { id: "11", value: "More than 10 years including the past 10 years" }
    ]);
  }

  getAppealDecisionBy(): Observable<ILookup[]> {
    return of([
      { id: "immi", value: "The Immigration Department" },
      { id: "minister", value: "The Minister" },
      { id: "tribunal", value: "Tribunal" },
      { id: "court", value: "Court" },
      { id: "other", value: "Other" },
      { id: "unsure", value: "I don't Know" }
    ]);
  }

  getDecisionNotifiedBy(): Observable<ILookup[]> {
    return of([
      { id: "post", value: "By Post" },
      { id: "email", value: "By Email" },
      { id: "fax", value: "By Fax" },
      { id: "person", value: "In Person" }
    ]);
  }
  
}
